import { useState } from "react";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { adminAuth } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable passphrase gate used across /admin/* pages.
 */
export default function AdminGate({ onAuthed, redirect }) {
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);

  const handleAuth = async () => {
    if (!passphrase.trim()) return;
    setBusy(true);
    try {
      await adminAuth(passphrase.trim());
      onAuthed?.();
    } catch (err) {
      toast.error("Wrong passphrase.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-gate">
      <header className="w-full max-w-screen-md mx-auto px-6 h-16 flex items-center">
        <Logo />
      </header>
      <div className="max-w-md mx-auto px-6 pt-12 page-fade">
        <div
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
          style={{ background: "var(--ta-primary)", color: "#fff" }}
        >
          <Lock size={24} strokeWidth={2.2} />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--ta-primary)" }}>
          Internal Admin
        </h1>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
          Enter the shared passphrase to continue{redirect ? ` to ${redirect}` : ""}.
        </p>

        <div
          className="mt-6 flex items-center gap-3 px-4 rounded-2xl bg-white border-2 focus-within:border-[var(--ta-primary)]"
          style={{ borderColor: "var(--ta-border)", minHeight: 64 }}
        >
          <ShieldCheck size={20} style={{ color: "var(--ta-primary)" }} />
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAuth()}
            placeholder="Passphrase"
            data-testid="admin-passphrase-input"
            className="w-full text-base bg-transparent outline-none py-4"
            style={{ color: "var(--ta-text)" }}
          />
        </div>

        <button
          onClick={handleAuth}
          disabled={busy || !passphrase.trim()}
          data-testid="admin-passphrase-submit"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 min-h-[60px] rounded-2xl text-lg font-medium text-white disabled:opacity-60"
          style={{ background: "var(--ta-primary)" }}
        >
          {busy ? <Loader2 size={20} className="animate-spin" /> : "Continue"}
        </button>

        <p className="mt-4 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
          Held in browser memory only for this tab. Cleared on reload or when you close the tab — no cookies, no localStorage, no sessionStorage.
        </p>
      </div>
    </div>
  );
}
